import { Box, Flex, Heading, Text, Textarea } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { updateProjectStatus } from "store/customerProject/reducer";
import { uploadProjectSelector } from "store/customerProject/selector";
import SelectionRadio from "../components/SelectionRadio";
import StepShell from "../components/StepShell";
import { ADD_ONS } from "../constants/addOns";
import {
  calculateOrderTotal,
  calculateStandardAddonCost,
  STANDARD_ADDON_MIN_USD,
} from "../constants/pricing";
import MailedHardCopyOptionRow from "../components/MailedHardCopyOptionRow";

function OptionRow(props: {
  title: string;
  description: string;
  price: string;
  badgeText?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const { title, description, price, badgeText, checked, onChange } = props;

  return (
    <Box
      borderRadius="12px"
      border={`1px solid ${checked ? "#3A7BFF" : "#E6ECF3"}`}
      bg={checked ? "#F5F9FF" : "#FFFFFF"}
      p="16px"
      onClick={() => onChange(!checked)}
      cursor="pointer"
    >
      <Flex alignItems="flex-start" gap="12px">
        <SelectionRadio checked={checked} />

        <Box flex="1" minW="0">
          <Flex
            alignItems="center"
            gap="8px"
            minW="0"
            flexWrap="wrap"
            rowGap="6px"
          >
            <Heading
              fontSize="18px"
              fontWeight="600"
              color="#0B0E10"
              noOfLines={1}
            >
              {title}
            </Heading>
            {badgeText ? (
              <Box
                px="10px"
                py="3px"
                maxW="114px"
                fontStyle="Semibold"
                borderRadius="4px"
                bg="#D6EFE1"
                color="#2B9057"
                fontSize="12px"
                fontWeight="600"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {badgeText}
              </Box>
            ) : null}
          </Flex>
          <Text
            mt="6px"
            fontSize="14px"
            fontWeight="400"
            color="#505E70"
            lineHeight="160%"
            maxW="369px"
          >
            {description}
          </Text>
        </Box>
        <Text
          fontSize="20px"
          fontWeight="600"
          color="#0B0E10"
          whiteSpace="nowrap"
        >
          {price}
        </Text>
      </Flex>
    </Box>
  );
}

export default function Step3Options(props: {
  onNext: () => void;
  onBack: () => void;
}) {
  const { onNext, onBack } = props;
  const uploadProject = useSelector(uploadProjectSelector);
  const dispatch = useDispatch();

  const pagesForAddOns = Math.max(0, Number(uploadProject.totalPage || 0) || 0);
  const wordsForAddOns = Math.max(0, Number(uploadProject.totalWords || 0) || 0);

  const visibleAddOns = ADD_ONS.filter(
    (a) => a.transitionType === uploadProject.transitionType,
  );

  const totalStandardSpeed = calculateOrderTotal({
    transitionType: uploadProject.transitionType,
    include_expedited_turnaround: "0",
    totalPage: uploadProject.totalPage,
    totalWords: uploadProject.totalWords,
    targetLanguage: uploadProject.targetLanguage || [],
    isNotarized: uploadProject.isNotarized,
    isMailHardCopy: uploadProject.isMailHardCopy,
    include_desktop_publishing: (uploadProject as any)
      .include_desktop_publishing,
    include_proofreading: (uploadProject as any).include_proofreading,
  });

  const totalExpressSpeed = calculateOrderTotal({
    transitionType: uploadProject.transitionType,
    include_expedited_turnaround: "1",
    totalPage: uploadProject.totalPage,
    totalWords: uploadProject.totalWords,
    targetLanguage: uploadProject.targetLanguage || [],
    isNotarized: uploadProject.isNotarized,
    isMailHardCopy: uploadProject.isMailHardCopy,
    include_desktop_publishing: (uploadProject as any)
      .include_desktop_publishing,
    include_proofreading: (uploadProject as any).include_proofreading,
  });

  const expeditedPrice = Math.max(0, totalExpressSpeed - totalStandardSpeed);

  // Validation: if Mailed Hard Copy is selected, all address fields must be filled
  const isMailedHardCopyValid = (() => {
    if (!uploadProject.isMailHardCopy) return true;
    const recipientName = (
      (uploadProject as any).mailedHardCopyRecipientName || ""
    ).trim();
    const streetAddress = (
      (uploadProject as any).mailedHardCopyStreetAddress || ""
    ).trim();
    const city = ((uploadProject as any).mailedHardCopyCity || "").trim();
    const zipCode = ((uploadProject as any).mailedHardCopyZipCode || "").trim();
    const state = ((uploadProject as any).mailedHardCopyState || "").trim();
    const country = ((uploadProject as any).mailedHardCopyCountry || "").trim();
    return (
      recipientName && streetAddress && city && zipCode && state && country
    );
  })();

  const canContinue =
    (uploadProject.documents?.length || 0) > 0 && isMailedHardCopyValid;

  return (
    <StepShell
      step={3}
      title="Add-ons"
      description="Customize your order with optional extras."
      primaryLabel="Continue to payment"
      onPrimary={onNext}
      secondaryLabel="Back"
      onSecondary={onBack}
      isPrimaryDisabled={!canContinue}
    >
      <Flex direction="column" rowGap="12px">
        {visibleAddOns.map((a) => {
          const checked = (() => {
            if (a.key === "notarization") return !!uploadProject.isNotarized;
            if (a.key === "mailedHardCopy")
              return !!uploadProject.isMailHardCopy;
            if (a.key === "expeditedTurnaround")
              return (
                (uploadProject as any).include_expedited_turnaround === true ||
                (uploadProject as any).include_expedited_turnaround === 1 ||
                (uploadProject as any).include_expedited_turnaround === "1"
              );
            if (a.key === "desktopPublishing")
              return !!(uploadProject as any).include_desktop_publishing;
            if (a.key === "proofreading")
              return !!(uploadProject as any).include_proofreading;
            return false;
          })();

          const price = (() => {
            if (typeof a.fixedUsd === "number")
              return `$${a.fixedUsd.toFixed(2)}`;
            // Standard add-ons: per-word with $10 floor.
            if (typeof a.perWordUsd === "number")
              return `$${calculateStandardAddonCost(a.perWordUsd, wordsForAddOns).toFixed(2)}`;
            // Certified expedited stays per-page.
            if (typeof a.perPageUsd === "number")
              return `$${(a.perPageUsd * pagesForAddOns).toFixed(2)}`;
            if (a.key === "expeditedTurnaround")
              return `$${expeditedPrice.toFixed(2)}`;
            return "$0.00";
          })();

          // Append rate + minimum on Standard add-ons so customers
          // understand why a tiny doc still costs $10.
          const description =
            typeof a.perWordUsd === "number"
              ? `${a.description} (+$${a.perWordUsd.toFixed(2)}/word \u00b7 $${STANDARD_ADDON_MIN_USD.toFixed(2)} minimum)`
              : a.description;

          const onChange = (v: boolean) => {
            if (a.key === "notarization")
              return dispatch(updateProjectStatus({ isNotarized: v }));
            if (a.key === "mailedHardCopy")
              return dispatch(updateProjectStatus({ isMailHardCopy: v }));
            if (a.key === "expeditedTurnaround")
              return dispatch(
                updateProjectStatus({
                  include_expedited_turnaround: v,
                }),
              );
            if (a.key === "desktopPublishing")
              return dispatch(
                updateProjectStatus({ include_desktop_publishing: v }),
              );
            if (a.key === "proofreading")
              return dispatch(updateProjectStatus({ include_proofreading: v }));
          };

          if (a.key === "mailedHardCopy") {
            return (
              <MailedHardCopyOptionRow
                key={`${a.transitionType}:${a.key}`}
                title={a.title}
                description={description}
                price={price}
                badgeText={a.badgeText}
                checked={checked}
                onChange={onChange}
                recipientName={
                  (uploadProject as any).mailedHardCopyRecipientName || ""
                }
                streetAddress={
                  (uploadProject as any).mailedHardCopyStreetAddress || ""
                }
                city={(uploadProject as any).mailedHardCopyCity || ""}
                zipCode={(uploadProject as any).mailedHardCopyZipCode || ""}
                state={(uploadProject as any).mailedHardCopyState || ""}
                country={(uploadProject as any).mailedHardCopyCountry || ""}
                onFieldChange={(field, value) =>
                  dispatch(updateProjectStatus({ [field]: value }))
                }
              />
            );
          }

          return (
            <OptionRow
              key={`${a.transitionType}:${a.key}`}
              title={a.title}
              description={description}
              price={price}
              badgeText={a.badgeText}
              checked={checked}
              onChange={onChange}
            />
          );
        })}

        <Box
          borderRadius="12px"
          mt="12px"
          border="1px solid #E6ECF3"
          bg="#F5F7FA"
          p="24px"
        >
          <Text fontSize="18px" fontWeight="600" color="#0B0E10" mb="24px">
            Additional information (optional)
          </Text>
          <Textarea
            value={uploadProject.text || ""}
            onChange={(e) =>
              // Write to both fields: order_notes (text) and
              // project_description, so the note shows on order details
              // and in status emails like the legacy checkout did.
              dispatch(
                updateProjectStatus({
                  text: e.target.value,
                  projectDescription: e.target.value,
                })
              )
            }
            placeholder="Provide any additional details our team should know..."
            bg="#FFF"
            borderColor="#E6ECF3"
            _focus={{ borderColor: "#2F80ED" }}
            minH="120px"
            fontSize="14px"
          />
        </Box>
      </Flex>
    </StepShell>
  );
}
